'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AVAILABLE_LOCALES, isLocale, Locale, messages, Translations } from '@/i18n/messages';

const LANGUAGE_STORAGE_KEY = 'whoplays-language';

type LanguageContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  messages: Translations;
  locales: ReadonlyArray<{ value: Locale; label: string }>;
};

const LanguageContext = createContext<LanguageContextValue>({
  locale: 'en',
  setLocale: () => undefined,
  messages: messages.en,
  locales: AVAILABLE_LOCALES,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    let initial: Locale | null = null;

    if (isLocale(stored)) {
      initial = stored;
    } else {
      const browser = window.navigator.language.slice(0, 2).toLowerCase();
      if (isLocale(browser as Locale)) {
        initial = browser as Locale;
      }
    }

    if (initial) {
      setLocaleState((current) => (initial && initial !== current ? initial : current));
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
    }
  }, [locale]);

  const updateLocale = useCallback((value: Locale) => {
    setLocaleState(value);
  }, []);

  const value = useMemo(
    () => ({
      locale,
      setLocale: updateLocale,
      messages: messages[locale],
      locales: AVAILABLE_LOCALES,
    }),
    [locale, updateLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslations() {
  return useContext(LanguageContext);
}
